import { GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useContext, useEffect, useMemo } from 'react';

import { ModalContext } from '~components/ModalProvider';
import BenefitsTable, { BenefitsProps } from '~components/Profile/BenefitsTable';
import Layout from '~components/ui/Layout';
import { getFromAPI } from '~utils/api/common';
import useAuthUser from '~utils/hooks/useAuthUser';
import normalizeBenefits from '~utils/normalizers/normalizeBenefits';

import { LoyaltyBenefit } from '~types';

export const getStaticProps: GetStaticProps = async () => {
  const benefits = await getFromAPI<LoyaltyBenefit[]>('/loyalty-benefits');
  return {
    props: {
      benefits: normalizeBenefits(benefits),
    },
    revalidate: 60 * 60 * 24,
  };
};

type Props = InferGetStaticPropsType<typeof getStaticProps>;

type BenefitLevel = Record<string, BenefitsProps>;

const Beneficios: NextPage<Props> = ({ benefits }) => {
  const context = useContext(ModalContext);
  const user = useAuthUser();
  const router = useRouter();

  useEffect(() => {
    if (user?.Profile?.loyaltyActivated) {
      router.push('/perfil');
    }
  }, [user, router]);

  const groupedBenefits = useMemo(() => {
    if (Array.isArray(benefits)) {
      return benefits.reduce((group, item) => {
        const {
          LoyaltyLevel: { vistaLevelId },
        } = item;
        const levelNumber = vistaLevelId;
        if (!group[levelNumber]) {
          const level = {
            data: [item],
            title: item.LoyaltyLevel.name,
            locked: levelNumber !== 1,
            subtitle: item.LoyaltyLevel.description,
          };

          group[levelNumber] = { ...level };
        } else {
          group[levelNumber]?.data?.push(item);
        }

        return group;
      }, {} as BenefitLevel);
    }

    return [];
  }, [benefits]);
  const levels = Object.keys(groupedBenefits);

  return (
    <Layout meta={{ title: 'Beneficios | Citicinemas' }}>
      <section className="container flex flex-col items-center space-y-8 pt-6 pb-12 md:pt-32">
        <Image
          src="/img/horizontalCitipremia.png"
          alt="Logo de tarjeta Citipremia"
          width="228"
          height="147"
          style={{ objectFit: 'contain' }}
        />
        <div className="space-y-4 text-center md:w-[683px]">
          <h4 className="h4 md:big-title">
            Acumula puntos y empieza a recibir mejores beneficios.
          </h4>
          <p className="text-lg">
            Al utilizar tu tarjeta Citipremia podras subir de nivel para obtener más beneficios.
            Consigue puntos y paga con ellos.
          </p>
        </div>
        {!user?.id && (
          <div className="flex flex-col items-center gap-x-5 space-y-8 md:flex-row md:space-y-0">
            <button
              className="btn btn-primary btn-xl md:btn-lg md:order-2"
              onClick={() => context.setModal('signup')}
            >
              Crea tu cuenta
            </button>
            <button className="btn btn-link md:order-1" onClick={() => context.setModal('login')}>
              Inicia sesión
            </button>
          </div>
        )}
        <h2 className="h2 my-0 text-center md:pt-16">Beneficios</h2>
        <div className="space-y-12">
          {levels.map((level, index) => {
            const data = (groupedBenefits as BenefitLevel)[level];
            return (
              <BenefitsTable
                key={data.title}
                data={data.data}
                title={data.title}
                subtitle={data.subtitle}
                level={`${index + 1}`}
              />
            );
          })}
        </div>
      </section>
    </Layout>
  );
};

export default Beneficios;
